import { FC, useMemo } from 'react';
import { Table } from '@dynamika-shared/ui/Table/Table';
import { NoContent } from '@dynamika-shared/ui/NoContent/NoContent';
import {
  SubscriptionResponse,
  SubscriptionContent,
} from '@dynamika-shared/types/subscription';
import { makeSubscriptionColumns } from './SubscriptionsTable.columns';
import { OnSortColumnType } from '@dynamika-shared/ui/Table/Table.types';
import { useMakePagination } from 'src/store-redux/paginationAndSort/paginationAndSort.slice';
import { CheckStrokeIcon, CloseIcon } from '@dynamika-shared/ui/icons';
import classNames from 'classnames';

export type SubscriptionsTableViewProps = {
  subscriptions: SubscriptionResponse;
  loading: boolean;
  onToggleSubscription: (subscription: SubscriptionContent) => void;
  onAction: (action: string, subscription: SubscriptionContent) => void;
  onSort: OnSortColumnType;
  sortState?: string;
  onSubscribeAll: () => void;
  onUnsubscribeAll: () => void;
  onPaginationChange?: (page: number, pageSize: number) => void;
};

type RecipientsListControlButtonsProps = {
  onSubscribeAll: () => void;
  onUnsubscribeAll: () => void;
  buttonStates: { canSubscribeAll: boolean; canUnsubscribeAll: boolean };
  loading: boolean;
};

export const SubscriptionsTableView: FC<SubscriptionsTableViewProps> = ({
  subscriptions,
  loading,
  onToggleSubscription,
  onAction,
  onSort,
  sortState,
  onSubscribeAll,
  onUnsubscribeAll,
  onPaginationChange,
}) => {
  const pagination = useMakePagination(
    'subscriptions',
    !!subscriptions?.totalElements,
    onPaginationChange
  );

  const columns = makeSubscriptionColumns(onToggleSubscription, onAction);

  const buttonStates = useMemo(() => {
    const hasUnsubscribed =
      subscriptions?.content?.some(sub => !sub.subscribed) ?? false;
    const hasSubscribed =
      subscriptions?.content?.some(sub => sub.subscribed) ?? false;
    return {
      canSubscribeAll: hasUnsubscribed,
      canUnsubscribeAll: hasSubscribed,
    };
  }, [subscriptions?.content]);

  const hasSubscriptions = (subscriptions?.totalElements ?? 0) > 0;

  return (
    <div>
      {hasSubscriptions && (
        <RecipientsListControlButtons
          onSubscribeAll={onSubscribeAll}
          onUnsubscribeAll={onUnsubscribeAll}
          buttonStates={buttonStates}
          loading={loading}
        />
      )}
      <Table
        columns={columns}
        data={subscriptions?.content}
        totalItems={subscriptions?.totalElements ?? 0}
        loading={loading}
        onSort={onSort}
        styles={{
          table: 'table-auto',
        }}
        sortState={sortState}
        pagination={pagination}
        noContentMessage={
          <NoContent
            iconName={'team'}
            title={
              <span className='text-1424 text-spanishGray'>
                No{' '}
                <span className='font-semibold text-chineseBlack'>
                  Subscriptions
                </span>{' '}
                here yet!
              </span>
            }
          >
            <></>
          </NoContent>
        }
      />
    </div>
  );
};

const RecipientsListControlButtons = ({
  onSubscribeAll,
  onUnsubscribeAll,
  buttonStates,
  loading,
}: RecipientsListControlButtonsProps) => {
  const subscribeAllDisabled = !buttonStates.canSubscribeAll || loading;
  const unsubscribeAllDisabled = !buttonStates.canUnsubscribeAll || loading;

  return (
    <div className='bg-blueTone rounded-sm h-[48px] px-2 flex items-center justify-between'>
      <h3 className='font-sans text-1824 font-medium text-black'>
        Recipients List
      </h3>
      <div className='flex gap-2 items-center'>
        <div className='flex gap-1 items-center'>
          <button
            onClick={onSubscribeAll}
            disabled={subscribeAllDisabled}
            className={classNames(
              'flex items-center gap-0.5 py-0.5 px-0 bg-transparent border-0 rounded-sm cursor-pointer',
              {
                'opacity-50 cursor-not-allowed': subscribeAllDisabled,
                'hover:bg-[#E8F0FE]': !subscribeAllDisabled,
              }
            )}
          >
            <CheckStrokeIcon
              width={16}
              height={16}
              className='text-dodgerBlue'
            />
            <span className='font-sans text-1424 font-medium text-dodgerBlue leading-[24px]'>
              Subscribe all
            </span>
          </button>
          <button
            onClick={onUnsubscribeAll}
            disabled={unsubscribeAllDisabled}
            className={classNames(
              'flex items-center gap-0.5 py-0.5 px-0 bg-transparent border-0 rounded-sm cursor-pointer',
              {
                'opacity-50 cursor-not-allowed': unsubscribeAllDisabled,
                'hover:bg-[#F5F5F5]': !unsubscribeAllDisabled,
              }
            )}
          >
            <CloseIcon width={16} height={16} className='text-spanishGray' />
            <span className='font-sans text-1424 font-medium text-spanishGray leading-[24px]'>
              Unsubscribe all
            </span>
          </button>
        </div>
      </div>
    </div>
  );
};
